// STEP 1'S EDGE PATHS — THE APP'S CARDS FOR WHEN THE RECORD IS NOT SIMPLY THE OWNER'S (WEB-PARITY-2A).
//
// In the app's screen order (SubjectPropertyView.swift:474-521): the record the owner is asked about — a near miss, or a
// building's parcels (DT-29, DT-30) — the refusal, the records service we could not reach, the building-scale record, the
// "enter these from your assessment notice" card (DT-28), the recent purchase (DT-31, DT-32) and the partial-records
// note; then, among the fields, the ratio from the owner's notice (DT-15) and the Jurisdiction card with the owner's own
// deadline (DT-23).
//
// WHICH card shows is the server's answer — `SubjectView.record`, NEW-7, V-5, the deadline's kind — read, never judged.
// Every word is the app's (`detailsWording.ts`, each pinned to its Swift line) or the server's (`Copy`, with its code).

import { useState, type ReactNode, type RefObject } from "react";
import type { Copy, ResolvePropertyResponse, SubjectView } from "@contract";
import { CopyLine } from "../../components/copy";
import { BrandCard, DateField, NumberField, TextField, ToggleField, Words } from "./detailsFields";
import {
  WEBSITE_REMINDERS_LIVE, candidateMatches, canRaiseSentenceId, deadlineNote, printableDeadlineDay, purchaseSentence,
  ratioPercentShown, ratioStatus, unitAssessed, unitCaption, unitDetail, unitExplanation, unitSale, type Correct,
  type Standing,
} from "./detailsRules";
import { words, wordsWith, type DetailsWordingId } from "./detailsWording";

type Decisions = ResolvePropertyResponse["decisions"];
type Body = ResolvePropertyResponse["body"];
type Candidate = Standing["unitCandidates"][number];

/** The app's advisory card: a tinted rounded rectangle, an icon, a title and its words (e.g. :1017-1044). `tone` is chrome. */
function Advisory({
  tone,
  glyph,
  titleId,
  children,
  card,
  code,
}: {
  tone: "caution" | "muted" | "primary" | "plain";
  glyph: "warn" | "question" | "retry" | "doc" | "building" | "pencil";
  titleId: DetailsWordingId;
  children: ReactNode;
  card: string;
  /** The server's own sentence for the decision this card answers, by code: on the element for us, as the page's words
   *  are the app's (the server's sentence says the same thing, and saying both would say it twice). */
  code?: string;
}) {
  return (
    <section className={`advisory ${tone}`} data-card={card} data-copy-code={code}>
      <span className={`advisory-glyph ${glyph}`} aria-hidden="true" />
      <div className="advisory-body">
        <Words id={titleId} as="b" />
        {children}
      </div>
    </section>
  );
}

// ── the record the owner is asked about (DT-29, DT-30) ──────────────────────────────────────────────────────────

/**
 * `addressMismatchCard` (SubjectPropertyView.swift:883-991). A near miss asks "Is this your property?" with a yes and a
 * no; a building whose record does not carry the owner's unit lists its parcels, and the ROWS ARE THE ANSWER — no
 * blanket "yes" while there is a row to pick (:974-975). A pick is confirmed before it is sent (`onPick`, the app's "File
 * against this parcel?"), because the row decides which home the appeal is filed against.
 */
export function RecordQuestionCard({
  standing,
  unit,
  onAnswer,
  onPick,
}: {
  standing: Standing;
  unit: string | null;
  onAnswer: (yes: boolean) => void;
  onPick: (c: Candidate) => void;
}) {
  const [filter, setFilter] = useState("");
  const isUnit = standing.question === "unit";
  const rows = standing.unitCandidates;
  const shown = filter.trim() === "" ? rows : rows.filter((c) => candidateMatches(c, filter));
  return (
    <Advisory tone="caution" glyph="warn" titleId="details.match.title" card="record-question">
      {isUnit ? (
        <p className="advisory-text" data-held-copy={unitExplanation(standing, unit).id}>{unitExplanation(standing, unit).text}</p>
      ) : (
        <p className="advisory-text" data-held-copy="details.match.nearMiss">
          {wordsWith("details.match.nearMiss", { address: standing.recordAddress ?? "" })}
        </p>
      )}
      {rows.length > 0 && (
        <div className="unit-picker" data-unit-count={rows.length} data-truncated={standing.unitCandidatesTruncated}>
          <Words id="details.unit.heading" as="b" />
          <p className="advisory-caption" data-held-copy="details.unit.caption">{unitCaption(standing)}</p>
          {rows.length > 6 && (
            <div className="unit-filter">
              <input
                type="text"
                inputMode="numeric"
                autoComplete="off"
                value={filter}
                placeholder={words("details.unit.filter")}
                data-held-copy-placeholder="details.unit.filter"
                aria-label={words("details.unit.filter")}
                onChange={(e) => setFilter(e.currentTarget.value)}
              />
              {filter !== "" && (
                <button type="button" className="link" aria-label={words("details.unit.filter.clear")} onClick={() => setFilter("")}>
                  ×
                </button>
              )}
            </div>
          )}
          {shown.length === 0 ? (
            <p className="advisory-caption" data-held-copy="details.unit.filter.none">
              {wordsWith("details.unit.filter.none", { filter: filter.trim(), count: rows.length })}
            </p>
          ) : (
            <div className="unit-rows">
              {shown.map((c) => (
                <button key={c.candidateId} type="button" className="unit-row" data-candidate={c.candidateId} onClick={() => onPick(c)}>
                  <UnitRow c={c} />
                </button>
              ))}
            </div>
          )}
        </div>
      )}
      <div className="advisory-actions">
        {rows.length === 0 && (
          <button type="button" className="btn primary" data-route-choice="record-yes" onClick={() => onAnswer(true)}>
            <Words id={isUnit ? "details.match.yes.unit" : "details.match.yes"} as="span" />
          </button>
        )}
        <button type="button" className="btn ghost" data-route-choice="record-no" onClick={() => onAnswer(false)}>
          <Words id={rows.length === 0 ? "details.match.no" : "details.match.none"} as="span" />
        </button>
      </div>
    </Advisory>
  );
}

/** `unitChoiceRow` (:2697-2744): the unit label leads where the county has one, then the assessed value, size and sale. */
function UnitRow({ c }: { c: Candidate }) {
  const unit = (c.unitNumber ?? "").trim();
  const detail = unitDetail(c);
  const sale = unitSale(c);
  const parcel = (c.parcelId ?? "").trim();
  return (
    <>
      <span className="unit-row-text">
        {unit !== "" ? (
          <>
            <b>{wordsWith("details.unit.row.unit", { unit })}</b>
            <span>{unitAssessed(c)}</span>
          </>
        ) : (
          <b>{unitAssessed(c)}</b>
        )}
        {detail !== null && <small>{detail}</small>}
        {sale !== null && <small>{sale}</small>}
        {parcel !== "" && <small className="muted">{wordsWith("details.unit.row.parcel", { parcel })}</small>}
      </span>
      <span className="chevron" aria-hidden="true">›</span>
    </>
  );
}

// ── the manual path (DT-28) ─────────────────────────────────────────────────────────────────────────────────────

/** `resolutionRefusalCard` (:1014-1045). The record was another property's; nothing of it is used. Its second button,
 *  "Check the address", is not here: the address is locked once a credit is applied (R112, the route half). */
export function RefusalCard({
  explanation,
  code,
  onEnter,
}: {
  explanation: { id: DetailsWordingId; text: string };
  code: string | undefined;
  onEnter: () => void;
}) {
  return (
    <Advisory tone="caution" glyph="question" titleId="details.refusal.title" card="refusal" code={code}>
      <p className="advisory-text" data-held-copy={explanation.id}>{explanation.text}</p>
      <div className="advisory-actions">
        <button type="button" className="btn primary" data-route-choice="enter-details" onClick={onEnter}>
          <Words id="details.refusal.enter" as="span" />
        </button>
      </div>
    </Advisory>
  );
}

/** `recordsUnavailableCard` (:1163-1197): a fact about US, not the property — retry first, the notice second. */
export function UnreachableCard({ onRetry, onManual, busy }: { onRetry: () => void; onManual: () => void; busy: boolean }) {
  return (
    <Advisory tone="muted" glyph="retry" titleId="details.unreachable.title" card="records-unreachable">
      <Words id="details.unreachable.body" as="p" className="advisory-text" />
      <div className="advisory-actions">
        <button type="button" className="btn primary" disabled={busy} data-route-choice="retry" onClick={onRetry}>
          <Words id="details.unreachable.retry" as="span" />
        </button>
        <button type="button" className="btn ghost" disabled={busy} data-route-choice="manual" onClick={onManual}>
          <Words id="details.unreachable.manual" as="span" />
        </button>
      </div>
    </Advisory>
  );
}

/** `buildingScaleCard` (:1135-1160). No "use it anyway": the figures are the building's. */
export function BuildingCard({ code }: { code: string | undefined }) {
  return (
    <Advisory tone="plain" glyph="building" titleId="details.building.title" card="building-scale" code={code}>
      <Words id="details.building.body" as="p" className="advisory-text" />
    </Advisory>
  );
}

/** `ownerSuppliedDetailsCard` (:1207-1245): why the figures are the owner's, said by why we are here. */
export function OwnerSuppliedCard({ unreachable, parcelEmpty }: { unreachable: boolean; parcelEmpty: boolean }) {
  return (
    <Advisory tone="primary" glyph="doc" titleId="details.owner.title" card="owner-supplied">
      <Words id={unreachable ? "details.owner.unreachable" : "details.owner.notFound"} as="p" className="advisory-text" />
      {parcelEmpty && <Words id="details.owner.noParcel" as="p" className="advisory-text" />}
    </Advisory>
  );
}

/** `partialRecordsNote` (:1372-1407). */
export function PartialRecordsNote({ titleId, bodyId, body }: { titleId: DetailsWordingId; bodyId: DetailsWordingId; body: string }) {
  return (
    <Advisory tone="primary" glyph="pencil" titleId={titleId} card="partial-records">
      <p className="advisory-text" data-held-copy={bodyId}>
        {body}
      </p>
    </Advisory>
  );
}

// ── the recent purchase (DT-31, DT-32) ──────────────────────────────────────────────────────────────────────────

/**
 * `recentPurchaseRefusalCard` (:751-829), with the app's three routes. The first two are the owner's answers the server
 * now takes (contract 2.15.0): "That sale isn't right" opens the sale's own price and date — the app's route scrolls to
 * a card with no sale field, the D9 defect not copied here — and "That wasn't an open-market sale" sends the explicit
 * answer that is the ONLY thing that lifts NEW-7 (silence keeps it armed, CLAUDE.md). The third continues on condition
 * or record grounds, as 1C built it.
 */
export function PurchaseCard({
  new7,
  sale,
  risk,
  groundsChosen,
  onGrounds,
  correct,
  cardRef,
}: {
  new7: Extract<Decisions["NEW-7"], { ran: true; outcome: "block" }>;
  sale: SubjectView["lastSale"];
  risk: Body["canRaise"]["risk"];
  groundsChosen: boolean;
  onGrounds: () => void;
  correct: Correct;
  cardRef: RefObject<HTMLDivElement | null>;
}) {
  const [editingSale, setEditingSale] = useState(false);
  const saleDay = sale?.date === null || sale?.date === undefined ? null : isoDay(sale.date);
  return (
    <div
      className="purchase-card"
      ref={cardRef}
      data-card="recent-purchase"
      data-grounds={groundsChosen ? "chosen" : "open"}
      data-copy-code={new7.copy.code}
    >
      <div className="purchase-head">
        <span className="advisory-glyph warn" aria-hidden="true" />
        <div>
          <Words id="details.purchase.title" as="b" />
          <p className="advisory-text" data-held-copy="details.purchase.paid">
            {new7.value == null ? null : purchaseSentence(new7.value, sale?.date ?? null)}
          </p>
          <Words id={canRaiseSentenceId(risk)} as="p" className="advisory-text" />
        </div>
      </div>
      <hr />
      <Words id="details.grounds.heading" as="h3" />
      <div className="routes-stack">
        <button type="button" className="route" data-route-choice="wrong-sale" aria-expanded={editingSale} onClick={() => setEditingSale((v) => !v)}>
          <Words id="details.purchase.wrongSale.title" as="b" />
          <Words id="details.purchase.wrongSale.detail" as="span" />
        </button>
        {editingSale && (
          <div className="sale-fields" data-sale-fields="open">
            <NumberField
              labelId="details.sale.price"
              value={sale?.price.amount ?? null}
              prefix="$"
              whole
              field="lastSalePrice"
              onCommit={(typed) => correct("lastSalePrice", typed === null ? undefined : { basis: "market", amount: typed })}
            />
            <DateField labelId="details.sale.date" value={saleDay} field="lastSaleDate" onCommit={(day) => correct("lastSaleDate", day ?? undefined)} />
          </div>
        )}
        <button type="button" className="route" data-route-choice="not-market" onClick={() => correct("lastSaleDistressed", true)}>
          <Words id="details.purchase.notMarket.title" as="b" />
          <Words id="details.purchase.notMarket.detail" as="span" />
        </button>
        <button type="button" className="route" data-route-choice="grounds" onClick={onGrounds}>
          <Words id="details.grounds.title" as="b" />
          <Words id="details.grounds.detail" as="span" />
        </button>
      </div>
      {groundsChosen && <Words id="details.grounds.chosen" as="p" className="chosen" />}
    </div>
  );
}

/** A provider's `yyyyMMdd` (or an owner's `YYYY-MM-DD`) as the date field's day, or null when it is not a whole day. */
function isoDay(raw: string): string | null {
  const digits = raw.replace(/\D/g, "");
  if (digits.length < 8) return null;
  return `${digits.slice(0, 4)}-${digits.slice(4, 6)}-${digits.slice(6, 8)}`;
}

// ── the ratio from the owner's notice (DT-15) ────────────────────────────────────────────────────────────────────

/**
 * `assessmentRatioCard` (:2006-2105), on the app's rule: only where the COUNTY's ratio is one the owner confirms from their
 * notice (V-5 `needsLocalRatio`, which the server keeps as the jurisdiction's even once the owner has entered their own —
 * the app's `jurisdictionRatio`, :1895-1917). The field is the owner's percent, the line under it the app's for where
 * things stand, and the footer the basis sentence. Nebraska shows no card.
 *
 * The ratio, its origin and its confidence stay on the element for us whether or not the card shows (DT-14).
 */
export function RatioCard({
  v5,
  inForce,
  check,
  state,
  today,
  correct,
}: {
  v5: Decisions["V-5"];
  inForce: Body["ratio"];
  check: SubjectView["ratioCheck"];
  state: string | null;
  today: string;
  correct: Correct;
}) {
  if (!v5.ran) return null;
  const ratio = v5.value;
  const marks = {
    "data-ratio-value": ratio.value,
    "data-ratio-origin": ratio.origin,
    "data-ratio-confidence": ratio.confidence,
    "data-ratio-in-force": inForce.value,
    "data-ratio-in-force-origin": inForce.origin,
  };
  if (ratio.confidence !== "needsLocalRatio") return <span hidden {...marks} />;
  const pa = (state ?? "").trim().toUpperCase() === "PA";
  const hasPrefill = ratio.value < 0.999;
  const entered = inForce.origin === "ownerEntered" ? ratioPercentShown(inForce.value) : null;
  const status = ratioStatus({ jurisdiction: ratio, inForce, check, pa, today });
  return (
    <BrandCard icon="percent" titleId="details.ratio.title" data-card="ratio" accent {...marks}>
      <NumberField
        labelId={hasPrefill && pa ? "details.ratio.field.pa" : "details.ratio.field.other"}
        value={entered}
        unitId="details.ratio.unit"
        field="ratioPercent"
        onCommit={(typed) => correct("ratioPercent", typed === null ? undefined : typed)}
      />
      <p className={`caption ${status.tone === "warn" ? "warn" : status.tone === "good" ? "good" : ""}`} data-held-copy={status.id}>
        {status.text}
      </p>
      {/* The app's footer is the JURISDICTION's basis sentence (`footer: jurisdictionRatio.basis`, :2034). */}
      <CopyLine copy={ratio.basis} className="card-footer" />
    </BrandCard>
  );
}

// ── Jurisdiction (DT-23) ────────────────────────────────────────────────────────────────────────────────────────

/**
 * `jurisdictionCard` (:2439-2481): the county and state the owner can correct, and the filing deadline from their notice.
 *
 * TWO THINGS ARE NOT THE APP'S, BOTH FOR F-EM-9. Its footer promises a reminder ("We remind you about 45 days before…"),
 * and every branch of the note under the switch is a reminder sentence (`deadlineEstimateNote`); the website sends no
 * reminders yet, so the footer renders only once `WEBSITE_REMINDERS_LIVE` is true, and the note is the app's own
 * non-reminder words for the same fact (`deadlineNote`, the Review screen's "Deadline" row).
 *
 * THE OWNER'S DATE IS STORED AS THE APP'S REMINDER READS IT: `appealDeadline`, a calendar day, sent with every call. The
 * switch seeds the printable day the server states for this county (`ReminderService.printableDeadline`, R122: never a
 * backstop's outer bound), else today — as the app's `hasDeadline` setter does (:2503-2523).
 */
export function JurisdictionCard({
  subject,
  deadline,
  today,
  correct,
}: {
  subject: SubjectView;
  deadline: Body["jurisdiction"]["deadline"];
  today: string;
  correct: Correct;
}) {
  const own = subject.appealDeadline ?? null;
  const note = own === null ? deadlineNote(deadline) : null;
  return (
    <BrandCard
      icon="columns"
      titleId="details.jurisdiction.title"
      footerId={WEBSITE_REMINDERS_LIVE ? "details.jurisdiction.footer" : undefined}
      data-card="jurisdiction"
    >
      <TextField
        labelId="details.jurisdiction.county"
        value={subject.county ?? ""}
        field="county"
        onCommit={(typed) => correct("county", typed.trim() === "" ? undefined : typed.trim())}
      />
      <TextField
        labelId="details.jurisdiction.state"
        value={subject.state ?? ""}
        field="state"
        onCommit={(typed) => correct("state", typed.trim() === "" ? undefined : typed.trim().toUpperCase())}
      />
      <ToggleField
        labelId="details.jurisdiction.hasDeadline"
        on={own !== null}
        field="hasDeadline"
        onChange={(on) => correct("appealDeadline", on ? printableDeadlineDay(deadline) ?? today : undefined)}
      />
      {own !== null ? (
        <DateField labelId="details.jurisdiction.deadline" value={own} field="appealDeadline" onCommit={(day) => correct("appealDeadline", day ?? undefined)} />
      ) : note === null ? null : note.kind === "estimate" ? (
        <p className="caption" data-held-copy="details.deadline.estimate" data-deadline-note="estimate">
          <Words id="details.deadline.title" as="b" /> {note.text}
        </p>
      ) : (
        <DeadlineCopy copy={note.copy} />
      )}
    </BrandCard>
  );
}

function DeadlineCopy({ copy }: { copy: Copy }) {
  return (
    <p className="caption" data-copy-code={copy.code} data-deadline-note="copy">
      {copy.text}
    </p>
  );
}
